import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllHotels, getProperties, getMinimumPrice } from '../../services/g2TravelApi';
import {
  HOTEL_SECTION_PLACEHOLDER_LARGE,
  resolveHotelSectionImage,
} from '../../lib/hotelSectionImages';
import { buildHotelDetailPath } from '../../lib/hotelRoutes';
import { useCurrency } from '../../context/CurrencyContext';
import './HomesGuestsLove.css';

const HomesGuestsLove = ({ onViewHotel }) => {
  const { formatFrom } = useCurrency();
  const navigate = useNavigate();
  const [homes, setHomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const carouselRef = useRef(null);

  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get rating text based on score
  const getRatingText = (score) => {
    if (score >= 9.0) return 'Wonderful';
    if (score >= 8.5) return 'Excellent';
    if (score >= 8.0) return 'Very Good';
    if (score >= 7.0) return 'Good';
    return 'Pleasant';
  };

  // Generate review counts similar to the image
  const getReviewCount = (index) => {
    const counts = [3223, 252, 795, 674, 1200, 890, 1500, 2100];
    return counts[index % counts.length];
  };

  useEffect(() => {
    const fetchHomes = async () => {
      setLoading(true);
      try {
        console.log('🔍 Fetching homes guests love...');

        let baseHotels = [];

        // Try full hotels endpoint first
        try {
          const allHotelsResponse = await getAllHotels({});
          if (allHotelsResponse?.success && Array.isArray(allHotelsResponse.hotels) && allHotelsResponse.hotels.length > 0) {
            baseHotels = allHotelsResponse.hotels;
          }
        } catch (err) {
          console.warn('⚠️ getAllHotels failed, falling back to getProperties:', err);
        }

        // Fallback to static properties list if needed
        if (!baseHotels.length) {
          const propertiesData = await getProperties();
          if (Array.isArray(propertiesData) && propertiesData.length > 0) {
            baseHotels = propertiesData;
          }
        }

        if (!Array.isArray(baseHotels) || baseHotels.length === 0) {
          console.warn('⚠️ No homes found from G2 Travel API');
          setHomes([]);
          return;
        }

        // Take first 8 homes for the carousel
        const selectedHomes = baseHotels.slice(0, 8);

        // Fetch images and prices for each home
        console.log('📸 Fetching images and prices from APIs...');
        const homesWithData = await Promise.all(
          selectedHomes.map(async (home, index) => {
            const homeName = home.name || home.propertyName || home.hotelName || 'Home';
            const imageUrl = resolveHotelSectionImage(home, index, 'large');

            // Fetch price from API
            let startingPrice = null;
            try {
              const homeId = home.id || home.propertyId || home.code;
              if (homeId) {
                // Use tomorrow as check-in date
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const checkInDate = tomorrow.toISOString().split('T')[0];
                
                const priceData = await getMinimumPrice(homeId, checkInDate, 1);
                
                if (priceData && priceData.minPrice && priceData.minPrice > 0) {
                  startingPrice = Math.round(priceData.minPrice);
                }
              }
            } catch (err) {
              console.warn(`⚠️ Failed to fetch price for ${homeName}:`, err);
            }

            // Generate default price if API didn't return
            if (!startingPrice) {
              const basePrice = 60 + (index * 50);
              startingPrice = basePrice;
            }

            // Generate rating (8.1 to 9.4 range)
            const rating = 8.1 + (Math.random() * 1.3);
            const roundedRating = Math.round(rating * 10) / 10;

            // Format location like in the image: "Old Town, Poland, Krakow" or "Kensington and Chelsea, United Kingdom, London"
            const city = home.cityName || home.city || '';
            const country = home.countryName || home.country || 'United Kingdom';
            const area = home.address1 || home.area || '';
            let formattedLocation = '';
            if (area && city && country) {
              formattedLocation = `${area}, ${country}, ${city}`;
            } else if (city && country) {
              formattedLocation = `${city}, ${country}`;
            } else {
              formattedLocation = country;
            }

            return {
              id: home.id || home.propertyId || home.code || `home-${index}`,
              name: homeName,
              image: imageUrl,
              location: formattedLocation,
              rating: roundedRating,
              ratingText: getRatingText(roundedRating),
              reviewCount: getReviewCount(index),
              startingPrice,
              homeData: home
            };
          })
        );

        console.log(`✅ Loaded ${homesWithData.length} homes with images and prices`);
        setHomes(homesWithData);
      } catch (error) {
        console.error('❌ Error fetching homes:', error);
        setHomes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHomes();
  }, []);

  const handleNext = () => {
    const maxIndex = Math.max(0, homes.length - 4);
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleHomeClick = (home) => {
    if (onViewHotel && home.id) {
      onViewHotel(home.id, home.homeData);
    } else {
      navigate(buildHotelDetailPath(home.id));
    }
  };

  const handleDiscoverClick = (e) => {
    e.preventDefault();
    navigate('/hotels');
    window.scrollTo(0, 0);
  };

  // On mobile, show all homes for horizontal scrolling
  const visibleHomes = isMobile ? homes : homes.slice(currentIndex, currentIndex + 4);
  const canGoNext = !isMobile && currentIndex < homes.length - 4;

  if (loading) {
    return (
      <section className="homes-guests-love">
        <div className="homes-guests-love__container">
          <div className="homes-guests-love__header">
            <h2 className="homes-guests-love__title">Homes guests love</h2>
            <a href="#" className="homes-guests-love__discover" onClick={handleDiscoverClick}>
              Discover homes
            </a>
          </div>
        </div>
      </section>
    );
  }

  if (homes.length === 0) {
    return null;
  }

  return (
    <section className="homes-guests-love">
      <div className="homes-guests-love__container">
        <div className="homes-guests-love__header">
          <h2 className="homes-guests-love__title">Homes guests love</h2>
          <a href="#" className="homes-guests-love__discover" onClick={handleDiscoverClick}>
            Discover homes
          </a>
        </div>

        <div className="homes-guests-love__wrapper" ref={carouselRef}>
          <div className="homes-guests-love__cards">
            {visibleHomes.map((home) => (
              <div
                key={home.id}
                className="homes-guests-love__card"
                onClick={() => handleHomeClick(home)}
              >
                <div className="homes-guests-love__card-image-wrapper">
                  <img
                    src={home.image}
                    alt={home.name}
                    className="homes-guests-love__card-image"
                    onError={(e) => {
                      e.target.src = HOTEL_SECTION_PLACEHOLDER_LARGE;
                    }}
                  />
                </div>
                <div className="homes-guests-love__card-content">
                  <h3 className="homes-guests-love__card-name">{home.name}</h3>
                  <p className="homes-guests-love__card-location">{home.location}</p>
                  <div className="homes-guests-love__card-rating">
                    <span className="homes-guests-love__card-rating-score">{home.rating}</span>
                    <span className="homes-guests-love__card-rating-text">{home.ratingText}</span>
                    <span className="homes-guests-love__card-reviews">{home.reviewCount.toLocaleString()} reviews</span>
                  </div>
                  <p className="homes-guests-love__card-price">
                    Starting from {formatFrom(home.startingPrice, 'GBP')}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {canGoNext && (
            <button
              className="homes-guests-love__arrow homes-guests-love__arrow--right"
              onClick={handleNext}
              aria-label="Next homes"
            >
              ›
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomesGuestsLove;
